/**
 * Choosing which standing contracts price this enquiry.
 *
 * Provision business runs on blanket orders: milk, bread and fruit have an agreed rate with a
 * named supplier, and a line for such an item should never go to RFQ. The purchaser picks the
 * contracts in play — several suppliers commonly cover one enquiry — and every matching line is
 * routed to `contract` at the contracted rate.
 *
 * ---- Why a selection and not a per-item lookup ----
 *
 * Asking "is this item on a contract?" per row costs a request per row for an answer that is
 * empty for most of the catalogue. Selecting contracts inverts it: a handful of requests fetch
 * a few hundred contracted codes once, and every line becomes a Map lookup. It also matches how
 * the work is actually done — a purchaser knows which suppliers hold the contracts.
 *
 * Grouped by supplier because that is what the purchaser recognises. Six suppliers hold two or
 * three orders each, so the order NAME alone ("MFG-BLR-2026-00034") is not something anyone can
 * choose by.
 */

import { useMemo, useState } from 'react'
import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'
import { BLANKET_ORDER_ACTIVE, type BlanketOrderSummary } from '../../lib/api/useItems'

export function BlanketOrderBar({
  orders,
  selected,
  loading,
  matchedLines,
  scope,
  selectedLineCount,
  onScopeChange,
  onChange,
}: {
  orders: BlanketOrderSummary[]
  selected: string[]
  loading: boolean
  /** How many of the enquiry's lines the current selection prices — the feedback that matters. */
  matchedLines: number
  /** Point 9 — whether contracts price every matching line or only the ticked ones. */
  scope: 'all' | 'selected'
  /** Ticked lines in the table, so 'selected' can explain itself when nothing is ticked. */
  selectedLineCount: number
  onScopeChange: (scope: 'all' | 'selected') => void
  onChange: (names: string[]) => void
}) {
  /*
    Grouped on the supplier LINK value, labelled with `supplier_name`. The live payload proves the
    two are not interchangeable — "SEA LAND FOODSTUFF TRADG LLC,AJMAN" is the docname, "SEA LAND
    FOODSTUFF TRADING LLC" is the company's actual name. Grouping on the label would merge two
    suppliers who happen to share one; showing the docname makes the purchaser read an abbreviation
    with a comma in it.
  */
  const bySupplier = useMemo(() => {
    const map = new Map<string, { label: string; orders: BlanketOrderSummary[] }>()
    for (const o of orders) {
      const group = map.get(o.supplier) ?? { label: o.supplier_name || o.supplier, orders: [] }
      group.orders.push(o)
      map.set(o.supplier, group)
    }
    return [...map.entries()]
      .map(([supplier, group]) => [supplier, group.orders, group.label] as const)
      .sort((a, b) => a[2].localeCompare(b[2]))
  }, [orders])

  const [open, setOpen] = useState(false)
  const chosen = new Set(selected)
  /** Only an Active contract may price a line — the rest are shown but never selectable. */
  const isActive = (o: BlanketOrderSummary) => o.status === BLANKET_ORDER_ACTIVE
  /** Suppliers with at least one contract ticked — what the closed control counts. */
  const suppliersChosen = bySupplier.filter(([, os]) => os.some((o) => chosen.has(o.name)))
  const toggleSupplier = (supplierOrders: BlanketOrderSummary[]) => {
    // Never ticks a cancelled or draft contract, even when the supplier row is toggled as a whole.
    const names = supplierOrders.filter(isActive).map((o) => o.name)
    const allOn = names.every((n) => chosen.has(n))
    onChange(allOn ? selected.filter((n) => !names.includes(n)) : [...new Set([...selected, ...names])])
  }

  if (orders.length === 0) return null

  const label =
    selected.length === 0
      ? 'Choose contract suppliers…'
      : `${suppliersChosen.length} supplier${suppliersChosen.length === 1 ? '' : 's'} · ${selected.length} contract${selected.length === 1 ? '' : 's'}`

  return (
    <div className="banner" style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b>📄 Contract suppliers</b>

        {/*
          A dropdown, not a row of chips: one provision enquiry routinely draws on SEVERAL
          contracts at once (milk from one supplier, bread from another, fruit from a third), and
          seventeen chips wrapping across the banner buried the enquiry's own summary underneath
          them. Closed it states the choice in one line; open it is a checklist.
        */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            style={{
              border: '1px solid var(--line)',
              background: 'var(--panel)',
              color: 'var(--ink)',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12.5,
              cursor: 'pointer',
              minWidth: 230,
              textAlign: 'left',
            }}
          >
            {label} <span style={{ float: 'right', color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
          </button>

          {open ? (
            <>
              {/* Click-away, behind the panel — a dropdown that only closes via its own button
                  traps anyone who opens it to look and then clicks elsewhere. */}
              <div
                onClick={() => setOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div
                style={{
                  position: 'absolute',
                  zIndex: 41,
                  top: 'calc(100% + 4px)',
                  left: 0,
                  minWidth: 340,
                  maxHeight: 320,
                  overflowY: 'auto',
                  background: 'var(--panel)',
                  border: '1px solid var(--line)',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                }}
              >
                {bySupplier.map(([supplier, allOrders, supplierLabel]) => {
                  const supplierOrders = allOrders.filter(isActive)
                  const dead = allOrders.filter((o) => !isActive(o))
                  const on = supplierOrders.length > 0 && supplierOrders.every((o) => chosen.has(o.name))
                  const partial = !on && supplierOrders.some((o) => chosen.has(o.name))
                  return (
                    <label
                      key={supplier}
                      className="supcheck"
                      style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 12px', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        // Nothing to tick when every one of this supplier's contracts is dead.
                        disabled={supplierOrders.length === 0}
                        // A supplier with some-but-not-all orders ticked is neither on nor off.
                        ref={(el) => { if (el) el.indeterminate = partial }}
                        onChange={() => toggleSupplier(allOrders)}
                      />
                      <span style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, opacity: supplierOrders.length === 0 ? 0.55 : 1 }}>
                          {supplierLabel}
                        </div>
                        {/* The order names, because several suppliers hold more than one contract
                            and the items differ between them. */}
                        {supplierOrders.length > 0 ? (
                          <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                            {supplierOrders.length === 1
                              ? supplierOrders[0].name
                              : `${supplierOrders.length} contracts · ${supplierOrders.map((o) => o.name).join(', ')}`}
                            {/* Only the method carries validity dates; the doctype fallback leaves
                                them blank, and a blank is silent rather than "valid to —". */}
                            {supplierOrders.some((o) => o.valid_till) ? (
                              <> · valid to {supplierOrders.map((o) => o.valid_till).filter(Boolean).join(', ')}</>
                            ) : null}
                          </div>
                        ) : null}
                        {/*
                          Contracts that exist but cannot be used, named with their status.

                          Hiding them read as "this supplier has no contract", when the truth is
                          "their contract was cancelled" — which sends a purchaser looking for
                          something that is right there and simply not usable. Shown, and not
                          selectable.
                        */}
                        {dead.length > 0 ? (
                          <div style={{ color: 'var(--warn)', fontSize: 11 }}>
                            {dead.map((o) => `${o.name} · ${o.status || 'not active'}`).join(', ')}
                          </div>
                        ) : null}
                      </span>
                    </label>
                  )
                })}
              </div>
            </>
          ) : null}
        </div>

        {/*
          Point 9. Shown only once contracts are actually ticked — before that there is nothing to
          scope, and an inert radio pair reads as a control that does not work.

          Radios rather than a checkbox: "all" and "selected" are both real, deliberate choices,
          and a checkbox labelled "only selected" leaves the default unnamed.
        */}
        {selected.length > 0 ? (
          <span style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={scope === 'all'} onChange={() => onScopeChange('all')} /> All matching lines
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={scope === 'selected'} onChange={() => onScopeChange('selected')} /> Ticked
              lines only
            </label>
          </span>
        ) : (
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            Matching lines are priced from these and skip the RFQ.
          </span>
        )}

        <span style={{ flex: 1 }} />
        {selected.length > 0 ? (
          <>
            <Badge tone={matchedLines > 0 ? 'violet' : 'muted'}>
              {loading ? 'loading contracts…' : `${matchedLines} line${matchedLines === 1 ? '' : 's'} priced`}
            </Badge>
            {/*
              The one state that would otherwise be silent: scoped to ticked lines with nothing
              ticked prices nothing, and without saying so the purchaser sees contracts selected,
              a zero badge, and no reason for it.
            */}
            {scope === 'selected' && selectedLineCount === 0 ? (
              <span style={{ color: 'var(--warn)', fontSize: 12 }}>Tick some lines, or switch to all.</span>
            ) : null}
            <Button size="sm" onClick={() => onChange([])}>
              Clear
            </Button>
          </>
        ) : null}
      </div>
    </div>
  )
}
